import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import OrderedSelection from "../components/OrderedSelection";
import {
  foyerApi,
  type RoomTypeRecord,
  type SubPageRow,
  type ScreenCategory,
  SCREEN_CATEGORIES,
} from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "../components/toast";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { usePermission } from "../auth/access";
import { ItemFormSurface, type Dismiss } from "../components/EditDrawer";
import { isBuiltinRoomTypeId } from "@shared/builtinRoomTypes";

/**
 * Admin-only catalogue of Room Types. Used by the per-premise
 * RoomsEditor as a dropdown when adding rooms. Non-admin users can
 * still GET the list (read-only) so the dropdown works, but only
 * admins reach this page (nav item is hidden) and the mutation
 * endpoints reject anyone else with 403.
 */
export default function RoomTypesPage() {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  // Gate on the capability, not the role - the server now enforces
  // `canManageRoomTypes` rather than a hand-rolled admin check, and the UI must
  // agree with it or the affordances drift from what the API actually allows.
  const isAdmin = usePermission("units", "administrate", "nenda");
  const [items, setItems] = useState<RoomTypeRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: RoomTypeRecord } | null
  >(null);
  /**
   * The product catalogue, held at PAGE level rather than inside the dialog.
   *
   * Both the table and the editor need it — the table to name what each type
   * grants, the editor to offer the choices — and fetching it once here means
   * the names are already on screen when a save closes the dialog, instead of
   * appearing a moment later as a second request lands.
   */
  // Distinguished from "there are none": the first is a permission the operator
  // does not hold, the second is a catalogue nobody has imported yet, and they
  // want different sentences.

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listRoomTypes();
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(t, e));
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Best-effort: reading it needs `canManageDistribution`, which a customer
    // holding `canManageRoomTypes` does not have. A 403 leaves the ids on
    // screen unresolved rather than breaking the page.
  }, []);

  async function onDelete(row: RoomTypeRecord) {
    const msg =
      row.roomCount > 0
        ? t("roomTypesPage.deleteConfirmInUse", {
            name: row.name,
            count: row.roomCount,
          })
        : t("roomTypesPage.deleteConfirm", { name: row.name });
    if (!window.confirm(msg)) return;
    try {
      await foyerApi.deleteRoomType(row.id);
      toast.success(t("roomTypesPage.deleted", { name: row.name }));
      await refresh();
    } catch (e) {
      toast.error(describeError(t, "deleteRoomType", e, { name: row.name }));
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("roomTypesPage.eyebrow")}
        title={t("roomTypesPage.title")}
      />
      <Typography variant="body2" color="text.secondary">
        {t("roomTypesPage.intro")}
      </Typography>

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="room-types-list">
            <TableHead>
              <TableRow>
                <TableCell>{t("common:labels.name")}</TableCell>
                <TableCell>{t("common:labels.description")}</TableCell>
                <Tooltip title={t("roomTypesPage.categoryTooltip")}>
                  <TableCell>{t("common:labels.category")}</TableCell>
                </Tooltip>
                <TableCell align="right">
                  {t("roomTypesPage.columns.unitsUsing")}
                </TableCell>
                {isAdmin && <TableCell sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  data-testid={`room-type-row-${row.id}`}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightMedium" }}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.description ?? ""}
                    </Typography>
                  </TableCell>
                  <TableCell data-testid={`room-type-category-${row.id}`}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        (SCREEN_CATEGORIES as readonly string[]).includes(
                          row.category
                        )
                          ? t(
                              `common:screenCategories.${row.category as ScreenCategory}`
                            )
                          : row.category
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={row.roomCount}
                    />
                  </TableCell>
                  {isAdmin && isBuiltinRoomTypeId(row.id) && (
                    <TableCell align="right">
                      <Chip size="small" label={t("roomTypesPage.fixed")} />
                    </TableCell>
                  )}
                  {isAdmin && !isBuiltinRoomTypeId(row.id) && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={t("common:buttons.edit")}>
                        <IconButton
                          size="small"
                          aria-label={t("roomTypesPage.editNamed", {
                            name: row.name,
                          })}
                          onClick={() =>
                            setDialog({ mode: "edit", record: row })
                          }
                          data-testid={`room-type-edit-${row.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:buttons.delete")}>
                        <IconButton
                          size="small"
                          aria-label={t("roomTypesPage.deleteNamed", {
                            name: row.name,
                          })}
                          onClick={() => onDelete(row)}
                          data-testid={`room-type-delete-${row.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 6 : 5}>
                    <Typography variant="caption" color="text.secondary">
                      {t("roomTypesPage.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dialog && (
        <RoomTypeDialog
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

function RoomTypeDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: RoomTypeRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  /** Fetched once by the page — the table needs it too. */
}) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  // Seeded once from `initial`. The caller mounts this dialog only when it has
  // a record, so the mount IS the seed — see [[cms-list-and-editor]].
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [category, setCategory] = useState(initial?.category ?? "room");
  const [pages, setPages] = useState<string[]>(() => initial?.pages ?? []);
  const [pageOptions, setPageOptions] = useState<SubPageRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Best-effort: a customer holding `canManageRoomTypes` but not
    // `canManageDistribution` gets a 403 and simply edits ids by hand.
    void foyerApi
      .listSubPages()
      .then((r) => setPageOptions(r.subPages))
      .catch(() => setPageOptions([]));
  }, []);

  async function save(dismiss: Dismiss) {
    setBusy(true);
    setErr(null);
    try {
      const desc = description.trim() || null;
      if (initial) {
        await foyerApi.updateRoomType(initial.id, {
          name,
          description: desc,
          category,
          pages,
          // The COMPLETE list, never a delta. `syncRoomEntitlement` replaces
          // rather than merges, so a partial list here is a silent revoke.
        });
        toast.success(t("roomTypesPage.updated", { name }));
      } else {
        await foyerApi.createRoomType({ name, description: desc, category });
        toast.success(t("roomTypesPage.added", { name }));
      }
      // Slide the form away first; the page then refreshes and unmounts it.
      dismiss(() => void onSaved());
    } catch (e) {
      setErr(friendlyError(t, e));
      toast.error(
        name.trim()
          ? describeError(t, "saveRoomTypeNamed", e, { name: name.trim() })
          : describeError(t, "saveRoomType", e)
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      onClose={onClose}
      title={
        initial ? t("roomTypesPage.editTitle") : t("roomTypesPage.newTitle")
      }
      maxWidth="sm"
      busy={busy}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={busy || !name.trim()}
            data-testid="room-type-save"
          >
            {busy ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t("common:labels.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          autoFocus
          placeholder={t("roomTypesPage.fields.namePlaceholder")}
          inputProps={{ "data-testid": "room-type-name" }}
        />
        <TextField
          label={t("roomTypesPage.fields.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          multiline
          minRows={2}
          placeholder={t("roomTypesPage.fields.descriptionPlaceholder")}
        />
        {/* Upstream's `RoomTypeCategory`. The difference is real: a lobby
              screen has no guest, no purchase affordance and no continue-
              watching, so it genuinely shows a different set of rails.
              🔗 Also THE CONNECTOR (2026-09-07): this is what tells a
              licence's screen category (Hotel/Care / Common area / Public
              viewing - same three values, same labels) which rooms of this
              type actually draw against it. A hotel with no need for named
              types just creates three, one per category; a care facility
              keeps its named wings (Memory Care, Assisted Living, …) for
              call-contact routing (`callDirectory.ts`) and gives each the
              right category here. */}
        <TextField
          select
          label={t("common:labels.category")}
          value={category}
          onChange={(e) => setCategory(e.target.value as ScreenCategory)}
          size="small"
          helperText={t("roomTypesPage.fields.categoryHelp")}
        >
          {SCREEN_CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {t(`common:screenCategories.${c}`)}
            </MenuItem>
          ))}
          {!(SCREEN_CATEGORIES as readonly string[]).includes(category) && (
            <MenuItem value={category}>{category}</MenuItem>
          )}
        </TextField>
        {initial && (
          <>
            <OrderedSelection
              label={t("roomTypesPage.fields.subPages")}
              hint={t("roomTypesPage.fields.subPagesHint")}
              value={pages}
              options={pageOptions.map((p) => ({
                id: p.id,
                label: p.name || p.id,
              }))}
              onChange={setPages}
              testId="room-type-pages"
            />
            {/* 🚨 The Products field is GONE, not hidden.
                  A room type decided what its rooms could PLAY until Sigge
                  ruled it out: a room type carries LAYOUT, so a content axis
                  multiplies it, and a room type is company-scoped or GLOBAL, so
                  content on a shared "Standard" leaked into customers who never
                  bought it. Entitlement follows the PREMISE LICENCE now.
                  Leaving the control here would have been worse than useless —
                  it wrote to a field nothing reads, so it looked like it worked
                  and did nothing. */}
            {/* 🚨 The Carousels field is GONE too, for the same reason.
                  A room type is company-scoped or GLOBAL, so an arrangement
                  authored on a shared "Standard" reached every customer using
                  it. The home page lives on the PREMISE now
                  (`Premise.carousels`, Streaming TV → Carousels) and nothing
                  reads `RoomType.carousels` any more. Sub-pages have NOT moved
                  — they are still layout, which is what a room type is for. */}
          </>
        )}
        {err && (
          <Typography variant="caption" color="error">
            {err}
          </Typography>
        )}
      </Stack>
    </ItemFormSurface>
  );
}
